import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ArrowDown2, SearchNormal1, TickCircle } from 'iconsax-reactjs';
import { Popover, Spinner, cx } from '@/components/ui';
import styles from './ContextSelector.module.css';

export interface ContextOption {
  id: string;
  label: string;
  description?: string;
}

export interface ContextSelectorProps {
  /** What is being selected, e.g. "Organization", "School", "Academic year". */
  label: string;
  /** Selected option id. */
  value: string | null;
  options: ContextOption[];
  onSelect: (id: string) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** Show a filter box. Defaults to true when there are more than 7 options. */
  searchable?: boolean;
  /** Decorative leading icon. */
  icon?: ReactNode;
  /** Hide the small label above the value (it stays available to screen readers). */
  hideLabel?: boolean;
  className?: string;
}

/**
 * Presentational selector for the working context (organization, school, academic year).
 * Disabled when there is only one option; filterable when there are many.
 */
export function ContextSelector({
  label,
  value,
  options,
  onSelect,
  loading = false,
  disabled = false,
  placeholder = 'Select…',
  searchable,
  icon,
  hideLabel = false,
  className,
}: ContextSelectorProps) {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isSearchable = searchable ?? options.length > 7;
  const selected = options.find((o) => o.id === value) ?? null;
  const isDisabled = disabled || loading || options.length <= 1;

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLocaleLowerCase().includes(q) ||
        (o.description?.toLocaleLowerCase().includes(q) ?? false),
    );
  }, [options, query]);

  const optionId = (index: number) => `${baseId}-opt-${index}`;
  const active = filtered[activeIndex];

  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  function openList() {
    if (isDisabled) return;
    setQuery('');
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((o) => o.id === value),
      ),
    );
    setOpen(true);
  }

  function choose(option: ContextOption | undefined) {
    if (!option) return;
    setOpen(false);
    if (option.id !== value) onSelect(option.id);
  }

  function onListKeyDown(event: KeyboardEvent<HTMLElement>, allowHomeEnd: boolean) {
    const last = filtered.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(last, i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        if (!allowHomeEnd) return;
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        if (!allowHomeEnd) return;
        event.preventDefault();
        setActiveIndex(last);
        break;
      case 'Enter':
      case ' ':
        if (event.key === ' ' && !allowHomeEnd) return; // typing a space in the filter
        event.preventDefault();
        choose(active);
        break;
      default:
        if (allowHomeEnd && event.key.length === 1 && /\S/.test(event.key)) {
          const char = event.key.toLocaleLowerCase();
          const start = activeIndex + 1;
          const ordered = [...filtered.slice(start), ...filtered.slice(0, start)];
          const match = ordered.find((o) => o.label.toLocaleLowerCase().startsWith(char));
          if (match) setActiveIndex(filtered.indexOf(match));
        }
    }
  }

  const valueText = loading ? 'Loading…' : (selected?.label ?? placeholder);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cx(styles.trigger, className)}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={isDisabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            openList();
          }
        }}
      >
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={styles.text}>
          <span className={cx(styles.label, hideLabel && 'sr-only')}>
            {label}
            <span className="sr-only">:</span>
          </span>{' '}
          <span className={cx(styles.value, !selected && styles.placeholder)}>{valueText}</span>
        </span>
        {loading ? (
          <Spinner size="xs" decorative />
        ) : (
          options.length > 1 && (
            <ArrowDown2
              size={14}
              color="currentColor"
              className={styles.chevron}
              aria-hidden="true"
            />
          )
        )}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        role="dialog"
        aria-label={`Select ${label}`}
        className={styles.panel}
      >
        {isSearchable && (
          <div className={styles.search}>
            <SearchNormal1 size={16} color="currentColor" aria-hidden="true" />
            <input
              type="text"
              role="combobox"
              aria-label={`Filter ${label}`}
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={active ? optionId(activeIndex) : undefined}
              className={styles.searchInput}
              placeholder={`Filter ${label.toLocaleLowerCase()}`}
              value={query}
              data-autofocus
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => onListKeyDown(event, false)}
            />
          </div>
        )}
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          tabIndex={isSearchable ? -1 : 0}
          aria-activedescendant={!isSearchable && active ? optionId(activeIndex) : undefined}
          className={styles.list}
          data-autofocus={isSearchable ? undefined : true}
          onKeyDown={(event) => onListKeyDown(event, true)}
        >
          {filtered.map((option, index) => (
            // Keyboard interaction is handled on the listbox / filter input (activedescendant).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={option.id}
              id={optionId(index)}
              data-index={index}
              role="option"
              tabIndex={-1}
              aria-selected={option.id === value}
              className={cx(styles.option, index === activeIndex && styles.optionActive)}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(option)}
            >
              <span className={styles.optionText}>
                <span className={styles.optionLabel}>{option.label}</span>
                {option.description && (
                  <span className={styles.optionDescription}>{option.description}</span>
                )}
              </span>
              {option.id === value && (
                <TickCircle
                  size={16}
                  color="currentColor"
                  variant="Bold"
                  className={styles.check}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className={styles.empty}>No matches</p>}
        </div>
      </Popover>
    </>
  );
}
